import SalesheetPDF from "../Salesheet/SalesheetPDF";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import ReactGA from "react-ga4";

const saleSheetUrl = "https://vgsa157d2e.execute-api.us-west-1.amazonaws.com/gpg/salesheetv2";
const productSpecUrl = "https://vgsa157d2e.execute-api.us-west-1.amazonaws.com/gpg/productspecs";

const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
            resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
            reject(error);
        };
    });
};

const getOrientationFromBuffer = (buffer) => {
    try {
        const view = new DataView(buffer);
        if (view.getUint16(0, false) !== 0xffd8) return 1;

        let offset = 2;
        while (offset < view.byteLength) {
            if (view.getUint8(offset) !== 0xff) break;

            const marker = view.getUint8(offset + 1);
            if (marker === 0xda || marker === 0xd9) break;

            const length = view.getUint16(offset + 2, false);
            if (marker === 0xe1) {
                const exifOffset = offset + 4;
                if (view.getUint32(exifOffset, false) !== 0x45786966) return 1;

                const tiffOffset = exifOffset + 6;
                const littleEndian =
                    view.getUint16(tiffOffset, false) === 0x4949;
                const firstIfdOffset = view.getUint32(
                    tiffOffset + 4,
                    littleEndian,
                );
                let dirOffset = tiffOffset + firstIfdOffset;
                const entries = view.getUint16(dirOffset, littleEndian);
                dirOffset += 2;

                for (let i = 0; i < entries; i += 1) {
                    const entryOffset = dirOffset + i * 12;
                    const tag = view.getUint16(entryOffset, littleEndian);
                    if (tag === 0x0112) {
                        return view.getUint16(entryOffset + 8, littleEndian);
                    }
                }

                return 1;
            }

            offset += 2 + length;
        }

        return 1;
    } catch {
        return 1;
    }
};

const getImageOrientation = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    return getOrientationFromBuffer(arrayBuffer);
};

const loadImage = (file) => {
    return new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(imageUrl);
            resolve(image);
        };

        image.onerror = (error) => {
            URL.revokeObjectURL(imageUrl);
            reject(error);
        };

        image.src = imageUrl;
    });
};

const applyOrientationTransform = (ctx, orientation, width, height) => {
    switch (orientation) {
        case 2:
            ctx.transform(-1, 0, 0, 1, width, 0);
            break;
        case 3:
            ctx.transform(-1, 0, 0, -1, width, height);
            break;
        case 4:
            ctx.transform(1, 0, 0, -1, 0, height);
            break;
        case 5:
            ctx.transform(0, 1, 1, 0, 0, 0);
            break;
        case 6:
            ctx.transform(0, 1, -1, 0, width, 0);
            break;
        case 7:
            ctx.transform(0, -1, -1, 0, width, height);
            break;
        case 8:
            ctx.transform(0, -1, 1, 0, 0, height);
            break;
        default:
            break;
    }
};

const normalizeImageOrientation = async (file) => {
    const orientation = await getImageOrientation(file);
    if (orientation === 1) return file;

    const image = await loadImage(file);
    const shouldSwapSize = orientation >= 5 && orientation <= 8;
    const canvas = document.createElement("canvas");
    canvas.width = shouldSwapSize ? image.height : image.width;
    canvas.height = shouldSwapSize ? image.width : image.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    applyOrientationTransform(ctx, orientation, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);

    const outputType =
        file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";
    const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, outputType, 0.92);
    });

    if (!blob) return file;
    return new File([blob], file.name, { type: blob.type || outputType });
};

const listSpec = (specs) => {
    const organizedData = specs.reduce((acc, spec) => {
        const groupName = spec.groupname.toUpperCase();
        const showname = spec.showname;
        const value = spec.value;

        if (!acc[groupName]) {
            acc[groupName] = [];
        }
        if (value !== "N/A") {
            acc[groupName].push({
                showname,
                value,
            });
        }

        return acc;
    }, {});

    const result = Object.entries(organizedData)
        .map(([groupName, specs]) => ({
            [groupName]: specs,
        }))
        .filter((obj) => Object.values(obj)[0].length > 0);

    return result;
};

export default async function CreateSaleSheet(img, customerName, email, country, product, model_id, model_name, type, onSuccess) {
    const normalizedImg = await normalizeImageOrientation(img).catch(() => img);
    const imgBase64Data = await convertBase64(normalizedImg);
    const blobURL = URL.createObjectURL(normalizedImg);

    let res = '';
    const data = {
        img: imgBase64Data,
        name: customerName,
        email: email,
        country: country,
        model: model_id,
        type: type,
    };
    res = await fetch(saleSheetUrl, { method: "post", body: JSON.stringify(data) });
    if (res.status === 200) {
        ReactGA.event("generate_sales_sheet", {
            customer: customerName,
            email: email,
            country: country,
            product_name: product,
            model: model_id,
            model_name: model_name,
            type: type,
        });
        onSuccess();
    }

    res = await fetch(productSpecUrl, {
        method: "post",
        body: JSON.stringify({ productName: product }),
    });

    const result = await res.json();
    const content = result.body.product;
    const spec = listSpec(result.body.product.specs);
    const url2 = localStorage.getItem("prodcut_img");

    const doc = (
        <SalesheetPDF
            imgUrl={blobURL}
            type={type}
            spec={spec}
            content={content}
            product={product}
            img1={blobURL}
            img2={url2}
        />
    );

    const pdfBlob = await pdf(doc).toBlob();
    const fileName = `${product}.pdf`;
    const blob = new Blob([pdfBlob], { type: "application/octet-stream" });
    saveAs(blob, fileName);
};